// src/components/Auth/ResetPassword.js
//
// The destination of the password-reset email. Supabase's recovery link
// carries a token that the client exchanges for a (recovery-scoped) session
// automatically via detectSessionInUrl — so by the time this mounts the user
// is authenticated and simply needs to set a new password.
//
// This route existed in the email (resetPasswordForEmail redirectTo
// `${origin}/reset-password`) but was never registered in App.js, so the link
// silently dropped users into the app with no way to set a password. Reuses
// Login.css so it matches the sign-in screen exactly.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../../config/supabase';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PasswordStrength from './PasswordStrength';
import './Login.css';

export default function ResetPassword() {
  useDocumentTitle('Set a new password');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  // null = still checking, true/false = resolved
  const [hasSession, setHasSession] = useState(null);
  const navigate = useNavigate();

  // The recovery session is established asynchronously from the URL, so wait
  // for either the session or the PASSWORD_RECOVERY event before deciding the
  // link is invalid — checking once on mount races and shows a false "expired".
  useEffect(() => {
    let cancelled = false;
    if (!supabase) { setHasSession(false); return undefined; }

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!cancelled && session) setHasSession(true);
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setHasSession((prev) => (prev === true ? prev : Boolean(data?.session)));
    });

    return () => { cancelled = true; sub?.subscription?.unsubscribe(); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      // Land them in the app; they're already authenticated.
      setTimeout(() => navigate('/home', { replace: true }), 1500);
    } catch (err) {
      setError(err?.message || 'Could not set your password. Request a new reset link and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">TekStak</h1>
          <p className="login-subtitle">Set a new password</p>
        </div>

        {!isSupabaseConfigured && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-yellow-400 text-sm font-medium">Authentication not configured</p>
          </div>
        )}

        {done && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-blue-400 text-sm">Password updated. Taking you to the app…</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {hasSession === false && !done && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">
              This reset link has expired or was already used. Request a new one from the sign-in screen.
            </p>
          </div>
        )}

        {!done && hasSession && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="login-input"
              disabled={loading}
            />
            <PasswordStrength password={password} />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              className="login-input"
              disabled={loading}
            />
            <button type="submit" disabled={loading} className="login-button">
              {loading ? 'Saving…' : 'Set password'}
            </button>
          </form>
        )}

        <div className="switcher text-center text-sm">
          <button onClick={() => navigate('/login')} className="link-button">
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
